/**
 * Server-safe twin of `brand.ts`.
 *
 * `brand.ts` exports React Logo components, which can't be imported from
 * server-only code paths (HQ's launcher-icon route, the apps-catalog
 * bootstrap, anything that needs the registry without dragging React in).
 * This file ships the same per-app metadata as plain values plus a static
 * `iconSvg` string — the same `<path>` data the matching React component
 * renders, packaged as standalone SVG markup.
 *
 * Scope: HQ's `/api/launcher-icons/<slug>.svg` route and the static
 * fallback brand behind signin cards (which draw the LIVE registry tile
 * first — see `AppBrand.registrySlug`). LAUNCHER TILE branding (name/
 * description/url/icon/colors shown in the sidebar rail, Cmd+K, and the home
 * "My Apps" grid) is owned by the multipass App registry and edited at
 * HQ → Apps. The marks and colors here predate that registry and no longer
 * match it; build-time favicons are rendered from a snapshot of the registry
 * (`scripts/registry-tiles.ts`), not from this file.
 * The React `brand.ts` re-exports name/host/etc. from this file so the two
 * never drift.
 */

export interface AppBrandMeta {
  /** Display name (sidebar, signin, launcher tile). */
  name: string
  /** Tile color key matching `SIDEBAR_TILE_COLORS`. */
  colorKey: string
  /** Soft tile background hex (Tailwind `<color>-100`). */
  bg: string
  /** Foreground / icon hex (Tailwind `<color>-600`). */
  fg: string
  /** Production host URL, no trailing slash. */
  host: string
  /** Path of the signin page on this app. */
  signinPath: '/signin' | '/login'
  /** One-line tagline for the launcher tile / catalog entry. */
  description: string
  /**
   * Standalone SVG markup for the brand mark, served as `<img>` from
   * `/api/launcher-icons/<slug>.svg`. Self-contained 256×256 tile with
   * `bg` rounded backdrop + `fg`-filled icon baked in — `<img>` strips
   * `currentColor` inheritance so we can't rely on parent CSS to tint it.
   */
  iconSvg: string
}

const FACTORY_PATH =
  'M120,176a12,12,0,0,1-12,12H80a12,12,0,0,1,0-24h28A12,12,0,0,1,120,176Zm56-12H148a12,12,0,0,0,0,24h28a12,12,0,0,0,0-24Zm68,52a12,12,0,0,1-12,12H24a12,12,0,0,1,0-24h4V88a12,12,0,0,1,19.2-9.6L92,112V88a12,12,0,0,1,19.2-9.6l33.33,25,10.61-74.23A20.1,20.1,0,0,1,174.94,12h18.12a20.1,20.1,0,0,1,19.8,17.17l15,105.13c0,.1.12,1.34.12,1.7v68h4A12,12,0,0,1,244,216Zm-77.57-96.17L172,124h30.16L189.59,36H178.41ZM52,204H204V148H168a12,12,0,0,1-7.2-2.4l-14.38-10.78-.06,0L116,112v24a12,12,0,0,1-19.2,9.6L52,112Z'
const TASKER_PATH =
  'M228,128a12,12,0,0,1-12,12H128a12,12,0,0,1,0-24h88A12,12,0,0,1,228,128ZM128,76h88a12,12,0,0,0,0-24H128a12,12,0,0,0,0,24Zm88,104H128a12,12,0,0,0,0,24h88a12,12,0,0,0,0-24ZM79.51,39.51,56,63l-7.51-7.52a12,12,0,0,0-17,17l16,16a12,12,0,0,0,17,0l32-32a12,12,0,0,0-17-17Zm0,64L56,127l-7.51-7.52a12,12,0,1,0-17,17l16,16a12,12,0,0,0,17,0l32-32a12,12,0,0,0-17-17Zm0,64L56,191l-7.51-7.52a12,12,0,1,0-17,17l16,16a12,12,0,0,0,17,0l32-32a12,12,0,0,0-17-17Z'
const ASK_PATH =
  'M232.49,215.51,185,168a92.12,92.12,0,1,0-17,17l47.53,47.54a12,12,0,0,0,17-17ZM44,112a68,68,0,1,1,68,68A68.07,68.07,0,0,1,44,112Z'
const AGENT_PATH =
  'M128,16 L139,102 L207,49 L154,117 L240,128 L154,139 L207,207 L139,154 L128,240 L117,154 L49,207 L102,139 L16,128 L102,117 L49,49 L117,102 Z'
const HQ_PATH =
  'M100,36H56A20,20,0,0,0,36,56v44a20,20,0,0,0,20,20h44a20,20,0,0,0,20-20V56A20,20,0,0,0,100,36ZM96,96H60V60H96ZM200,36H156a20,20,0,0,0-20,20v44a20,20,0,0,0,20,20h44a20,20,0,0,0,20-20V56A20,20,0,0,0,200,36Zm-4,60H160V60h36Zm-96,40H56a20,20,0,0,0-20,20v44a20,20,0,0,0,20,20h44a20,20,0,0,0,20-20V156A20,20,0,0,0,100,136Zm-4,60H60V160H96Zm104-60H156a20,20,0,0,0-20,20v44a20,20,0,0,0,20,20h44a20,20,0,0,0,20-20V156A20,20,0,0,0,200,136Zm-4,60H160V160h36Z'
const COMMS_PATH =
  'M230.14,25.86a20,20,0,0,0-19.57-5.11l-.22.07L18.44,79a20,20,0,0,0-3.06,37.25L99,157l40.71,83.65a19.81,19.81,0,0,0,18,11.38c.57,0,1.15,0,1.73-.07A19.82,19.82,0,0,0,177,237.56L235.18,45.65a1.42,1.42,0,0,0,.07-.22A20,20,0,0,0,230.14,25.86ZM156.91,221.07l-34.37-70.64,46-45.95a12,12,0,0,0-17-17l-46,46L34.93,99.09,210,46Z'
const RFPS_PATH =
  'M216.49,79.52l-56-56A12,12,0,0,0,152,20H56A20,20,0,0,0,36,40V216a20,20,0,0,0,20,20H200a20,20,0,0,0,20-20V88A12,12,0,0,0,216.49,79.52ZM160,57l23,23H160ZM60,212V44h76V92a12,12,0,0,0,12,12h48V212Zm112-80a12,12,0,0,1-12,12H96a12,12,0,0,1,0-24h64A12,12,0,0,1,172,132Zm0,40a12,12,0,0,1-12,12H96a12,12,0,0,1,0-24h64A12,12,0,0,1,172,172Z'
const ROAD_WARRIOR_PATH =
  'M208,28H188V24a12,12,0,0,0-24,0v4H92V24a12,12,0,0,0-24,0v4H48A20,20,0,0,0,28,48V208a20,20,0,0,0,20,20H208a20,20,0,0,0,20-20V48A20,20,0,0,0,208,28ZM68,52a12,12,0,0,0,24,0h72a12,12,0,0,0,24,0h16V76H52V52ZM52,204V100H204V204Z'
const PREVIEWS_PATH =
  'M241,150.65s0,0,0-.05a51.33,51.33,0,0,0-2.53-5.9L196.93,50.18a12,12,0,0,0-2.5-3.65,36,36,0,0,0-50.92,0A12,12,0,0,0,140,55V76H116V55a12,12,0,0,0-3.51-8.48,36,36,0,0,0-50.92,0,12,12,0,0,0-2.5,3.65L17.53,144.7A51.33,51.33,0,0,0,15,150.6s0,0,0,.05A52,52,0,1,0,116,168V100h24v68a52,52,0,1,0,101-17.35ZM80,62.28a12,12,0,0,1,12-1.22v63.15a51.9,51.9,0,0,0-35.9-7.62ZM64,196a28,28,0,1,1,28-28A28,28,0,0,1,64,196ZM164,61.06a12.06,12.06,0,0,1,12,1.22l23.87,54.31a51.9,51.9,0,0,0-35.9,7.62ZM192,196a28,28,0,1,1,28-28A28,28,0,0,1,192,196Z'
// Phosphor "ForkKnife" bold path — menus has no hand-drawn logo component,
// so the brand mark is the Phosphor glyph its tile uses.
const MENUS_PATH =
  'M68,88V40a12,12,0,0,1,24,0V88a12,12,0,0,1-24,0ZM220,40V224a12,12,0,0,1-24,0V180H152a12,12,0,0,1-12-12,273.23,273.23,0,0,1,7.33-57.82C157.42,68.42,176.76,40.33,203.27,29A12,12,0,0,1,220,40ZM196,62.92C182.6,77,175,98,170.77,115.38A254.41,254.41,0,0,0,164.55,156H196ZM128,39A12,12,0,0,0,104,41l4,47.46a28,28,0,0,1-56,0L56,41A12,12,0,1,0,32,39L28,87c0,.34,0,.67,0,1a52.1,52.1,0,0,0,40,50.59V224a12,12,0,0,0,24,0V138.59A52.1,52.1,0,0,0,132,88c0-.33,0-.66,0-1Z'
const CUSTOMERS_STOREFRONT_PATH =
  'M236,96a12,12,0,0,0-.44-3.3L221.2,42.51A20.08,20.08,0,0,0,202,28H54A20.08,20.08,0,0,0,34.8,42.51L20.46,92.7A12,12,0,0,0,20,96v16a43.94,43.94,0,0,0,16,33.92V216a12,12,0,0,0,12,12H208a12,12,0,0,0,12-12V145.92A43.94,43.94,0,0,0,236,112ZM57.05,52H199l9.14,32H47.91Zm91,56v4a20,20,0,0,1-40,0v-4ZM53,128.71A20,20,0,0,1,44,112v-4H84v4a20,20,0,0,1-20,20,19.76,19.76,0,0,1-9.07-2.2A11.54,11.54,0,0,0,53,128.71ZM196,204H60V155.81c1.32.12,2.65.19,4,.19a43.86,43.86,0,0,0,32-13.85,43.89,43.89,0,0,0,64,0A43.86,43.86,0,0,0,192,156c1.35,0,2.68-.07,4-.19Zm16-92a20,20,0,0,1-9,16.71,11.66,11.66,0,0,0-1.88,1.09A20,20,0,0,1,172,112v-4h40Z'

/**
 * Compose a self-contained tile SVG: 25%-rounded `bg` rect (rx=64/256 —
 * matches `AppTileIcon` / `AppSidebarTile`'s `borderRadius: size * 0.25`)
 * + `fg`-filled icon path scaled to 62.5% and centered. Same geometry as
 * the generator's `buildMasterSvg` (packages/generator/src/favicon.ts) and
 * `appFaviconSvg`, so scaffolded favicons, registry favicons, and launcher
 * tiles share one tile shape.
 */
function svg(path: string, bg: string, fg: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" rx="64" fill="${bg}"/><g transform="translate(48 48) scale(0.625)"><path d="${path}" fill="${fg}"/></g></svg>`
}

export const APP_BRANDS_META = {
  factory: {
    name: 'Factory',
    colorKey: 'indigo',
    bg: '#E0E7FF',
    fg: '#4F46E5',
    host: 'https://factory.ordo.com',
    signinPath: '/signin',
    description: 'Agent orchestration',
    iconSvg: svg(FACTORY_PATH, '#E0E7FF', '#4F46E5'),
  },
  tasker: {
    name: 'Tasks',
    colorKey: 'amber',
    bg: '#FEF3C7',
    fg: '#D97706',
    host: 'https://tasks.ordo.com',
    signinPath: '/signin',
    description: 'Task management & tickets',
    iconSvg: svg(TASKER_PATH, '#FEF3C7', '#D97706'),
  },
  'total-recall': {
    name: 'Ask',
    colorKey: 'teal',
    bg: '#CCFBF1',
    fg: '#0D9488',
    host: 'https://ask.ordo.com',
    signinPath: '/signin',
    description: 'Process & document center',
    iconSvg: svg(ASK_PATH, '#CCFBF1', '#0D9488'),
  },
  agent: {
    name: 'Agent',
    colorKey: 'indigo',
    bg: '#E0E7FF',
    fg: '#4F46E5',
    host: 'https://agent.ordo.com',
    signinPath: '/signin',
    description: 'AI agent — search, ask, act across every Ordo app',
    iconSvg: svg(AGENT_PATH, '#E0E7FF', '#4F46E5'),
  },
  hq: {
    name: 'HQ',
    colorKey: 'slate',
    bg: '#E2E8F0',
    fg: '#334155',
    host: 'https://hq.ordo.com',
    signinPath: '/signin',
    description: 'Internal team portal',
    iconSvg: svg(HQ_PATH, '#E2E8F0', '#334155'),
  },
  boombox: {
    name: 'Communications',
    colorKey: 'purple',
    bg: '#F3E8FF',
    fg: '#9333EA',
    host: 'https://comms.ordo.com',
    signinPath: '/login',
    description: 'Reminder & transactional system',
    iconSvg: svg(COMMS_PATH, '#F3E8FF', '#9333EA'),
  },
  'rfp-gremlin': {
    name: 'RFPs',
    colorKey: 'blue',
    bg: '#DBEAFE',
    fg: '#2563EB',
    host: 'https://rfps.ordo.com',
    signinPath: '/login',
    description: 'RFP analysis & submission',
    iconSvg: svg(RFPS_PATH, '#DBEAFE', '#2563EB'),
  },
  'road-warrior': {
    name: 'Schedule',
    colorKey: 'orange',
    bg: '#FFEDD5',
    fg: '#EA580C',
    host: 'https://schedule.ordo.com',
    signinPath: '/login',
    description: 'Travel & event coordination',
    iconSvg: svg(ROAD_WARRIOR_PATH, '#FFEDD5', '#EA580C'),
  },
  previews: {
    name: 'Previews',
    colorKey: 'lime',
    bg: '#ECFCCB',
    fg: '#65A30D',
    host: 'https://previews.ordo.com',
    signinPath: '/signin',
    description: 'Per-branch preview environments',
    iconSvg: svg(PREVIEWS_PATH, '#ECFCCB', '#65A30D'),
  },
  menus: {
    name: 'Menus',
    colorKey: 'green',
    bg: '#DCFCE7',
    fg: '#166534',
    host: 'https://menubuilder.ordo.com',
    signinPath: '/signin',
    description: 'Menu builder and manager application.',
    iconSvg: svg(MENUS_PATH, '#DCFCE7', '#166534'),
  },
  customers: {
    name: 'Customers',
    colorKey: 'pink',
    bg: '#FCE4EC',
    fg: '#C62828',
    host: 'https://customers.ordo.com',
    signinPath: '/signin',
    description: 'Customer operations console',
    iconSvg: svg(CUSTOMERS_STOREFRONT_PATH, '#FCE4EC', '#C62828'),
  },
} as const satisfies Record<string, AppBrandMeta>

export type AppBrandMetaKey = keyof typeof APP_BRANDS_META
